import type { AdminRole, AdminUser, UserWithPassword } from '@diora/contracts';
import { formatDateTime } from '@diora/contracts';
import { KeyRound, LogOut, Plus, Users as UsersIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/app-shell';
import { FormPage, RecordPage, useDirty, useRecord } from '@/components/form-page';
import { ConfirmDialog } from '@/components/records';
import {
  ActionMenu,
  Alert,
  Badge,
  Button,
  DataTable,
  EmptyState,
  Field,
  Input,
  Modal,
  Pagination,
  Select,
  SkeletonRows,
  Switch,
  Toolbar,
} from '@/components/ui';
import { apiPatch, apiPost, useAdminList, useAdminMutation, useListParams } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';
import { useToast } from '@/providers/toast';

const URL = '/admin/users';
/** The list this screen's form pages return to. */
const LIST = '/users';
const INVALIDATE = [['users'], ['user'], qk.readiness];
const ROLES: AdminRole[] = ['OWNER', 'EDITOR', 'VIEWER'];

export function UsersPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { user: me } = useAuth();
  const { notify } = useToast();

  const [params, setParams] = useListParams();
  const list = useAdminList<AdminUser>(qk.users(params), URL, params);
  const invalidate = INVALIDATE;

  const [generated, setGenerated] = useState<UserWithPassword | null>(null);
  const [revoking, setRevoking] = useState<AdminUser | null>(null);

  const reset = useAdminMutation(
    (row: AdminUser) => apiPost<UserWithPassword>(`${URL}/${row.id}/reset-password`, { expectedVersion: row.version }),
    {
      invalidate,
      onSuccess: (result) => setGenerated(result.data),
    },
  );

  const revoke = useAdminMutation(
    (row: AdminUser) => apiPost(`${URL}/${row.id}/revoke-tokens`, { expectedVersion: row.version }),
    {
      successMessage: t('users.revokeTokensDone'),
      invalidate,
      onSuccess: () => setRevoking(null),
    },
  );

  return (
    <>
      <PageHeader
        title={t('users.title')}
        description={t('users.subtitle')}
        actions={
          <Link to={`${LIST}/new`} className="dio-btn dio-btn--primary">
            <Plus className="dio-icon" aria-hidden="true" />
            {t('users.newTitle')}
          </Link>
        }
      />

      <Alert tone="info">{t('users.passwordsAreGenerated')}</Alert>

      <Toolbar />

      {list.isLoading ? (
        <SkeletonRows />
      ) : list.rows.length === 0 ? (
        <EmptyState icon={<UsersIcon aria-hidden="true" />} title={t('users.empty')} />
      ) : (
        <DataTable
          caption={t('users.title')}
          columns={[
            t('users.displayName'),
            t('users.role'),
            t('users.lastLogin'),
            <span className="sr-only">{t('common.actions')}</span>,
          ]}
        >
          {list.rows.map((row) => (
            <tr key={row.id}>
              <td>
                <div className="dio-table__name">{row.displayName}</div>
                {/* An address is always Latin script and always reads
                    left-to-right, whichever language the interface is in. */}
                <div className="dio-table__sub" dir="ltr">
                  {row.email}
                </div>
              </td>

              <td>
                <div className="flex flex-wrap items-center gap-1">
                  <Badge tone="outline">{t(`users.${row.role}`)}</Badge>
                  {!row.isActive && (
                    <Badge tone="danger" dot>
                      {t('users.inactive')}
                    </Badge>
                  )}
                  {row.mustChangePassword && (
                    <Badge tone="warning" dot>
                      {t('users.mustChangePassword')}
                    </Badge>
                  )}
                  {me?.id === row.id && <Badge tone="accent">{t('app.account')}</Badge>}
                </div>
              </td>

              <td className="dio-table__sub tabular">
                {row.lastLoginAt ? formatDateTime(row.lastLoginAt, locale) : t('users.neverSignedIn')}
              </td>

              <td className="dio-table__actions">
                <div className="dio-actions">
                  <Link to={`${LIST}/${row.id}`} className="dio-btn dio-btn--secondary dio-btn--sm">
                    {t('common.edit')}
                  </Link>
                  {/*
                    A user has no publication lifecycle, so this menu is built
                    from the two account actions directly rather than through
                    RowActions. Both are destructive enough to be worth one
                    extra click: a reset invalidates the current password, and a
                    revoke signs the person out everywhere.
                  */}
                  <ActionMenu
                    label={t('common.openMenuFor', { name: row.displayName })}
                    items={[
                      {
                        label: t('users.resetPassword'),
                        icon: <KeyRound className="dio-icon" aria-hidden="true" />,
                        disabled: reset.isPending,
                        onSelect: () => reset.mutate(row),
                      },
                      {
                        label: t('users.revokeTokens'),
                        icon: <LogOut className="dio-icon dio-icon--directional" aria-hidden="true" />,
                        danger: true,
                        onSelect: () => setRevoking(row),
                      },
                    ]}
                  />
                </div>
              </td>
            </tr>
          ))}
        </DataTable>
      )}

      <Pagination
        page={params.page ?? 1}
        pageSize={params.pageSize ?? 20}
        total={list.total}
        onPage={(page) => setParams({ page })}
      />

      <GeneratedPasswordDialog
        result={generated}
        onClose={() => setGenerated(null)}
        onCopied={() => notify(t('common.copied'), 'success')}
      />

      <ConfirmDialog
        open={revoking !== null}
        title={t('users.revokeTokens')}
        body={t('errors.REFRESH_TOKEN_REUSED')}
        confirmLabel={t('users.revokeTokens')}
        busy={revoke.isPending}
        onCancel={() => setRevoking(null)}
        onConfirm={() => revoking && revoke.mutate(revoking)}
      />
    </>
  );
}

export function UserFormPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { user: me } = useAuth();

  const record = useRecord<AdminUser>(qk.user(id ?? ''), URL, id);

  if (!id) return <UserForm user={null} isSelf={false} />;

  return (
    <RecordPage title={t('users.editTitle')} backTo={LIST} record={record}>
      {(row) => <UserForm key={row.id} user={row} isSelf={me?.id === row.id} />}
    </RecordPage>
  );
}

function UserForm({ user, isSelf }: { user: AdminUser | null; isSelf: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useToast();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<AdminRole>(user?.role ?? 'EDITOR');
  const [isActive, setIsActive] = useState(user?.isActive ?? true);

  /*
   * The generated password is shown here rather than back on the list.
   *
   * It exists exactly once, in the response to this one request — leaving the
   * page before it has been read loses it for good, and the account is then one
   * nobody can sign in to. So the account is created, the password is shown,
   * and only closing that dialog returns to the list.
   */
  const [generated, setGenerated] = useState<UserWithPassword | null>(null);

  const dirty = useDirty({ displayName, email, role, isActive });

  const save = useAdminMutation(
    () =>
      user
        ? apiPatch<AdminUser>(`${URL}/${user.id}`, {
            expectedVersion: user.version,
            displayName,
            role,
            isActive,
          })
        : apiPost<UserWithPassword>(URL, { displayName, email, role }),
    {
      invalidate: INVALIDATE,
      onSuccess: (result) => {
        if (user) navigate(LIST);
        else setGenerated(result.data as UserWithPassword);
      },
      successMessage: user ? t('common.saved') : undefined,
    },
  );

  return (
    <>
      <FormPage
        title={user ? t('users.editTitle') : t('users.newTitle')}
        description={t('users.subtitle')}
        backTo={LIST}
        dirty={dirty && generated === null}
        saving={save.isPending}
        canSave={displayName.trim().length > 0 && (Boolean(user) || email.trim().length > 0)}
        onSave={() => save.mutate(undefined)}
      >
        <Field label={t('users.displayName')} htmlFor="user-name" required>
          <Input id="user-name" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        </Field>

        <Field label={t('users.email')} htmlFor="user-email" required>
          <Input
            id="user-email"
            type="email"
            dir="ltr"
            // The address identifies the account; changing it would silently
            // move a session to a different person.
            disabled={Boolean(user)}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field
          label={t('users.role')}
          htmlFor="user-role"
          hint={
            role === 'OWNER'
              ? t('users.roleOwnerHint')
              : role === 'EDITOR'
                ? t('users.roleEditorHint')
                : t('users.roleViewerHint')
          }
        >
          <Select
            id="user-role"
            value={role}
            // Demoting yourself is refused by the API; disabling it here means
            // the refusal never has to be explained.
            disabled={isSelf}
            onChange={(event) => setRole(event.target.value as AdminRole)}
          >
            {ROLES.map((option) => (
              <option key={option} value={option}>
                {t(`users.${option}`)}
              </option>
            ))}
          </Select>
        </Field>

        {user && (
          <Switch
            checked={isActive}
            disabled={isSelf}
            onChange={setIsActive}
            label={t('users.active')}
            hint={isSelf ? t('errors.CANNOT_DEACTIVATE_SELF') : undefined}
          />
        )}

        {!user && <Alert tone="info">{t('users.passwordsAreGenerated')}</Alert>}
      </FormPage>

      <GeneratedPasswordDialog
        result={generated}
        onClose={() => navigate(LIST)}
        onCopied={() => notify(t('common.copied'), 'success')}
      />
    </>
  );
}

/**
 * The one-time password.
 *
 * Shown once, copied by hand, and never retrievable. It is deliberately not
 * emailed — there is no mail transport in this product, and adding one to send
 * credentials would be the worst possible first use of it.
 */
function GeneratedPasswordDialog({
  result,
  onClose,
  onCopied,
}: {
  result: UserWithPassword | null;
  onClose: () => void;
  onCopied: () => void;
}) {
  const { t } = useTranslation();
  if (!result) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={t('users.generatedTitle')}
      footer={
        <div className="flex justify-end gap-2">
          <Button
            onClick={() => {
              void navigator.clipboard?.writeText(result.temporaryPassword).then(onCopied);
            }}
          >
            {t('common.copy')}
          </Button>
          <Button variant="primary" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <Alert tone="warning">{t('users.generatedBody')}</Alert>
        <p className="dio-meta" dir="ltr">{result.email}</p>
        <code
          className="dio-input tabular block select-all"
          dir="ltr"
          style={{ fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}
        >
          {result.temporaryPassword}
        </code>
        <p className="dio-hint">{t('users.generatedNote')}</p>
      </div>
    </Modal>
  );
}
